import { timeOfDayLabelBySegment } from '../config/detailedForecast.config';

export const getTimeOfDayLabel = (forecast) => {
  return forecast?.day_segment || timeOfDayLabelBySegment[forecast?.segment] || '';
};

export const getForecastForDay = (forecast = [], selectedDay) => {
  return forecast.filter((forecastItem) => forecastItem.weekday === selectedDay);
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T.*?(?:([+-])(\d{2}):?(\d{2})|Z)?$/i;

/**
 * Reads the calendar date straight from the `iso8601` string instead of converting it to the browser's
 * timezone, so the date matches the forecast location (and the item's `weekday`).
 * Also returns the item's UTC offset in minutes (null when the string has none).
 */
const parseForecastDate = (iso8601) => {
  const match = ISO_DATE_PATTERN.exec(String(iso8601 ?? '').trim());
  if (!match) return null;

  const [, year, month, day, sign, offsetHours, offsetMinutes] = match;
  let offset = null;
  if (sign) offset = (sign === '-' ? -1 : 1) * (Number(offsetHours) * 60 + Number(offsetMinutes));
  else if (/Z$/i.test(String(iso8601).trim())) offset = 0;

  return { year: Number(year), month: Number(month), day: Number(day), offset };
};

/**
 * Builds the day selector tabs for the days present in the forecast data.
 *
 * `option` is the weekday name (the value stored in the `day` URL param and used by `getForecastForDay`),
 * `label` is the display text: "Today" for the current date at the forecast location, otherwise
 * "Thursday 10/1". Tabs are unique per weekday, dated by the first forecast item of that weekday.
 *
 * @param {Array} forecast - Forecast items containing `weekday` and `iso8601`.
 * @param {Date} [now] - Reference time used to determine "today".
 * @returns {{ option: string, label: string }[]} One tab per unique forecast weekday, in data order.
 */
export const getDetailedForecastDayTabs = (forecast = [], now = new Date()) => {
  const seenWeekdays = new Set();
  const tabs = [];

  forecast.forEach((forecastItem) => {
    const weekday = forecastItem?.weekday;
    const date = parseForecastDate(forecastItem?.iso8601);
    if (!date || !weekday || seenWeekdays.has(weekday)) return;
    seenWeekdays.add(weekday);

    // "Now" expressed in the forecast location's timezone (falls back to the browser's when no offset).
    const nowParts =
      date.offset === null
        ? { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
        : (() => {
            const shifted = new Date(now.getTime() + date.offset * 60000);
            return {
              year: shifted.getUTCFullYear(),
              month: shifted.getUTCMonth() + 1,
              day: shifted.getUTCDate(),
            };
          })();
    const isToday =
      date.year === nowParts.year && date.month === nowParts.month && date.day === nowParts.day;

    tabs.push({
      option: weekday,
      label: isToday ? 'Today' : `${weekday} ${date.month}/${date.day}`,
    });
  });

  return tabs;
};

/**
 * Returns one forecast item for each time of day.
 *
 * Each time of day contains the LAST forecast item in the array that matches it.
 * Earlier matching items are intentionally overwritten.
 *
 * If no forecast item matches a time of day, that value remains `null`.
 *
 * @param {Array} forecast - Forecast items to categorize by time of day.
 * @returns {{
 *   morning: Object|null,
 *   afternoon: Object|null,
 *   evening: Object|null,
 *   night: Object|null
 * }} The last matching forecast item for morning, afternoon, evening, and night.
 */
export const getForecastByTimeOfDay = (forecast = []) => {
  return forecast.reduce(
    (previousTimesOfDay, forecastItem) => {
      const timeOfDay = getTimeOfDayLabel(forecastItem);

      if (timeOfDay === 'Morning') previousTimesOfDay.morning = forecastItem;
      if (timeOfDay === 'Afternoon') previousTimesOfDay.afternoon = forecastItem;
      if (timeOfDay === 'Evening') previousTimesOfDay.evening = forecastItem;
      if (timeOfDay === 'Night') previousTimesOfDay.night = forecastItem;

      return previousTimesOfDay;
    },
    {
      morning: null,
      afternoon: null,
      evening: null,
      night: null,
    },
  );
};

export const getTimeOfDaySections = (timeOfDayForecast) => {
  return [
    ['Morning', timeOfDayForecast.morning],
    ['Afternoon', timeOfDayForecast.afternoon],
    ['Evening', timeOfDayForecast.evening],
    ['Night', timeOfDayForecast.night],
  ];
};
